<template>
  <div>
    <el-form-item :label="label">
      <el-select
        :size="size"
        style="width: 100%"
        filterable
        remote
        :default-active-first-option="false"
        :show-arrow="false"
        :not-found-content="null"
        :remote-method="handleSearch"
        @change="handleChange"
        :loading="loading"
        v-model="description"
        :placeholder="placeholder"
      >
        <!--  -->
        <template #empty>
          <i>Could not find a matching location.</i>
        </template>
        <!--  -->
        <el-option
          v-for="d in searchResults"
          :value="d.place_id"
          :label="d.display_name"
          :key="d.place_id"
        >
          <el-space>
            <i class="App-Loc-Icon el-icon-location" />
            <span>{{ d.display_name }}</span>
          </el-space>
        </el-option>
      </el-select>
    </el-form-item>
  </div>
</template>

<script lang="ts">
import Axios from "axios";
import { countries } from "countries-list";
import debounce from "lodash/debounce";
import { Options, Vue } from "vue-class-component";
import { Emit, Prop, Watch } from "vue-property-decorator";

/**
 * A single OpenStreetMap Nominatim search result.
 * https://nominatim.org/release-docs/latest/api/Search/
 */
interface NominatimResult {
  place_id: number;
  lat: string;
  lon: string;
  display_name: string;
  address?: Record<string, string>;
}

interface LocationGive {
  /**
   * Long, lat
   */
  coordinates: [number, number];
  country: string;
  city?: string;
  description?: string;
}

/**
 * Location search, backed by OpenStreetMap's Nominatim geocoder instead of
 * the Google Maps JS SDK.
 *
 * Previous implementation loaded the Google Maps script, faked a reactive
 * `window.google` via a getter/setter, and stored SDK service instances
 * (AutocompleteService/Geocoder) in Vue-reactive state - all fragile, and
 * ultimately blocked on a hardcoded API key that doesn't work for this
 * environment (no request was ever dispatched, silently). Nominatim needs no
 * key/SDK/script tag, so none of that machinery exists here.
 *
 * @improve Nominatim's public instance is rate-limited (~1 req/s) and its
 * usage policy (https://operations.osmfoundation.org/policies/nominatim/)
 * isn't meant for production traffic at scale - fine for now, but self-host
 * Nominatim or move to a paid geocoder (Mapbox/LocationIQ/OpenCage) before
 * relying on this in production.
 */
@Options({
  emits: ["update:modelValue"],
})
export default class extends Vue {
  @Prop({ type: Object, default: () => ({} as Record<string, any>) })
  modelValue!: Record<string, any>;

  @Prop({ default: "default", type: String })
  size!: string;

  @Prop({ default: "Search for location", type: String })
  placeholder!: string;

  @Prop({ default: "Location", type: String })
  label!: string;

  /**
   * Bound to the select's own input, decoupled from `modelValue` - only a
   * confirmed selection (see `handleChange`) updates `modelValue`.
   */
  description = "";
  searchResults: NominatimResult[] = [];
  loading = false;

  mounted() {
    this.description = this.modelValue?.description || "";
  }

  @Watch("modelValue")
  onModelValueChanged() {
    this.description = this.modelValue?.description || "";
  }

  /**
   * Debounced so we don't hammer Nominatim on every keystroke.
   */
  debouncedFetch = debounce((searchTxt: string) => this.fetch(searchTxt), 400);

  /**
   * @param searchTxt
   */
  handleSearch(searchTxt: string) {
    if (!searchTxt) {
      this.searchResults = [];
      return;
    }
    this.loading = true;
    this.debouncedFetch(searchTxt);
  }

  /**
   * fetch places
   */
  fetch(searchTxt: string) {
    Axios.get<NominatimResult[]>("https://nominatim.openstreetmap.org/search", {
      params: {
        format: "jsonv2",
        addressdetails: 1,
        limit: 8,
        q: searchTxt,
      },
    })
      .then((resp) => {
        this.searchResults = resp.data || [];
      })
      .catch((err) => {
        console.log("Failed to search locations ", err);
        this.searchResults = [];
      })
      .finally(() => {
        this.loading = false;
      });
  }

  /**
   * Called when a suggestion is selected
   * @param placeId
   */
  handleChange(placeId: number) {
    const result = this.searchResults.find((r) => r.place_id === placeId);
    if (!result) return;
    this.description = result.display_name;
    this.changeLocation(result);
  }

  /**
   * Called to finalise/reformulate the location with geometry info
   * @param result
   */
  @Emit("update:modelValue")
  changeLocation(result: NominatimResult): LocationGive {
    return this.formulateLocation(result);
  }

  /**
   * @formulate location
   */
  formulateLocation(result: NominatimResult): LocationGive {
    const address = result.address || {};
    // Resolve via the country's alpha-2 code against `countries-list` (the
    // same package the backend validates `location.country` with), rather
    // than trusting Nominatim's free-text `address.country` to already match
    // the exact spelling the backend expects.
    const countryCode = address.country_code?.toUpperCase();
    const country =
      (countryCode &&
        (countries as Record<string, { name: string }>)[countryCode]?.name) ||
      address.country;
    return {
      country,
      city: address.city || address.town || address.village || address.county,
      description: result.display_name,
      coordinates: [Number(result.lon), Number(result.lat)],
    };
  }
}
</script>

<style lang="scss">
.App-Loc-Icon {
  opacity: 0.6;
  color: rgba($--color-primary, 0.9);
}
</style>
